import { Box, Header, SpaceBetween, Tabs, Button } from '@amzn/awsui-components-react';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, isNull } from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { push } from 'connected-react-router';
import {
  getImportTypeMetadataForHeader,
  isImportInProgress,
  isImportFailed,
  isImportTriggered,
  processUserLevelImportMetadata,
  getImportFailureNotification,
} from '../../../import_data/utils/import_utils';
import { SUPPORTED_IMPORT_TYPES } from '../../../import_data/constants/import_types';
import { setPageNotification } from '../../../../common/components/with_page/redux/reducer';
import { getAllImportMetadataAction } from '../../../import_data/redux/GetAllImportMetadata';
import RecipientsTab from './components/recipients_tab/RecipientsTab';
import NotificationsOverview from './components/Overview/NotificationsOverview';
import Page from '../../../../common/components/with_page/Page';
import { RESOURCE } from '../../../auth/constants/rbac_constants';
import { BRC_ITEMS_PAGE } from '../../../../common/components/with_page/components/bread_crumb/constants';
import CCTab from './components/cc_tab/CCTab';
import { getStudyPeriod } from '../../../../common/constants/study_period';
import { getNotificationStatusType } from './common/util/utils';
import { NOTIFICATION_STATUS_TYPES } from './common/constants/constants';
import NotificationsDeleteModal from './components/Overview/NotificationsDeleteModal';
import { deleteNotificationAction } from './components/Overview/redux/deleteNotifications';
import { getRoutePathWithStudyPeriod, ROUTE_PATH } from '../../../../common/constants/route_paths';
import { createNotificationScheduleAction } from './components/Overview/redux/createNotificationSchedule';
import {
  getNotificationDetailsAction,
  notificationDetailActions,
} from './components/Overview/redux/getNotificationDetails';
import { cancelScheduledNotificationAction } from './components/Overview/redux/cancelScheduledNotification';
import NotificationContentTab from './components/notification_content_tab/NotificationContentTab';

const RiseNotificationsPage = ({
  history,
  studyPeriod,
  deleteNotificationBegin,
  navigateTo,
  createNotificationScheduledBegin,
  getNotificationDetailsBegin,
  notificationDetailsData,
  setNotificationStatus,
  cancelScheduledNotification,
  entityId,
  studyType,
  importMetadata,
  pushNotification,
  getImportDataBegin,
}) => {
  const notificationId = get(history, 'location.state', '') || '';
  const supportedImportTypes = SUPPORTED_IMPORT_TYPES.NOTIFICATION_SURVEY_FILTER;
  const [latestImportTypeMetadata, setLatestImportTypeMetadata] = React.useState(null);
  const [hasImportDataResponse, setHasImportDataResponse] = React.useState(false);

  useEffect(() => {
    getNotificationDetailsBegin({
      body: {
        notificationId,
      },
    });

    // Add delay to allow DynamoDB entry to be created after import trigger
    const timer = setTimeout(() => {
      getImportDataBegin({
        body: {
          importTypes: supportedImportTypes,
          includeAllUsers: true,
        },
      });
    }, 5000);

    return () => clearTimeout(timer);
  }, [getNotificationDetailsBegin, getImportDataBegin, notificationId, supportedImportTypes]);

  // Process import metadata - Filter by current notificationId
  useEffect(() => {
    if (!isEmpty(importMetadata)) {
      setHasImportDataResponse(true); // Mark response received

      console.log('[Import Status] Total import records received:', importMetadata.length);
      console.log('[Import Status] Current notificationId:', notificationId);

      // CRITICAL: Filter records for THIS notification only
      const notificationSpecificRecords = importMetadata.filter(record => {
        const recordNotificationId = get(record, 'importMetadata.notificationId', null);
        // Only include records that belong to the current notification
        return recordNotificationId === notificationId;
      });

      console.log(
        '[Import Status] Filtered records for this notification:',
        notificationSpecificRecords.length,
      );
      console.log('[Import Status] Notification-specific records:', notificationSpecificRecords);

      // Convert new API response format to old format for backward compatibility
      const convertedMetadata = notificationSpecificRecords.map(record => ({
        importType: record.importType,
        importStatus: record.importStatus,
        lastTriggerDate: record.importDateTime,
        lastImportDate: record.importDateTime,
        isImportDisabled: false,
        userLevelImportMetadata: {
          importStatus: record.importStatus,
          failedImportTemplateURL: record.failedTemplateURL,
        },
      }));

      const importTypeMetadata = getImportTypeMetadataForHeader(
        convertedMetadata,
        supportedImportTypes,
      );

      console.log('[Import Status] Selected import metadata:', importTypeMetadata);
      console.log(
        '[Import Status] Import status for buttons:',
        importTypeMetadata ? importTypeMetadata.importStatus : 'No import data',
      );

      setLatestImportTypeMetadata(importTypeMetadata);

      // Check for failures in notification-specific records only
      const userLevelImportMetadata = processUserLevelImportMetadata(convertedMetadata);
      if (!isNull(userLevelImportMetadata)) {
        const failureNotification = getImportFailureNotification({
          tableIdentifier: 'ValariNotificationsTable',
          ...userLevelImportMetadata,
          supportedImportTypes,
        });

        if (failureNotification && failureNotification.content) {
          pushNotification(failureNotification);
        }
      }
    }
  }, [importMetadata, notificationId, supportedImportTypes, pushNotification]);

  const isEditOrConfirmDisabled = () => {
    // Disable by default until import data response is received
    if (!hasImportDataResponse) return true;

    const config = { importTypeMetadata: latestImportTypeMetadata };
    return isImportTriggered(config) || isImportInProgress(config) || isImportFailed(config);
  };

  const isDeleteDisabled = () => {
    // Disable by default until import data response is received
    if (!hasImportDataResponse) return true;

    const config = { importTypeMetadata: latestImportTypeMetadata };
    return isImportTriggered(config) || isImportInProgress(config);
  };

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const showDeleteModal = () => {
    setIsDeleteModalVisible(true);
  };
  const onCancelDeleteEvent = () => {
    setIsDeleteModalVisible(false);
  };
  const onConfirmDeleteEvent = () => {
    deleteNotificationBegin({
      body: {
        notificationId,
      },
      onSuccess: () => {
        navigateTo({
          pathname: getRoutePathWithStudyPeriod(ROUTE_PATH.RISE_NOTIFICATION_PAGE, studyPeriod),
        });
      },
    });
  };
  const confirmScheduledDeliveryHandler = () => {
    createNotificationScheduledBegin({
      body: {
        notificationId,
      },
      onSuccess: updatedStatus => {
        setNotificationStatus(updatedStatus);
      },
    });
  };
  const editNotificationHandler = () => {
    navigateTo({
      pathname: getRoutePathWithStudyPeriod(ROUTE_PATH.CREATE_RISE_NOTIFICATION, studyPeriod),
      state: notificationDetailsData,
    });
  };
  const cancelScheduledHandler = () => {
    cancelScheduledNotification({
      body: {
        notificationId,
      },
      onSuccess: updatedStatus => {
        setNotificationStatus(updatedStatus);
      },
    });
  };
  const getNotificationActions = () => {
    const status = get(notificationDetailsData, 'status', 'UNKNOWN');
    const statusType = getNotificationStatusType(status);

    if (statusType === NOTIFICATION_STATUS_TYPES.IN_EDIT) {
      return (
        <SpaceBetween size="xs" direction="horizontal">
          <Button onClick={showDeleteModal} disabled={isDeleteDisabled()}>
            Delete
          </Button>
          <Button onClick={editNotificationHandler} disabled={isEditOrConfirmDisabled()}>
            Edit
          </Button>
          <Button
            variant="primary"
            onClick={confirmScheduledDeliveryHandler}
            disabled={isEditOrConfirmDisabled()}
          >
            Confirm scheduled delivery
          </Button>
        </SpaceBetween>
      );
    }
    if (
      statusType === NOTIFICATION_STATUS_TYPES.READY ||
      statusType === NOTIFICATION_STATUS_TYPES.IN_PROGRESS
    ) {
      return <Button onClick={cancelScheduledHandler}>Cancel scheduled delivery</Button>;
    }
    if (
      statusType === NOTIFICATION_STATUS_TYPES.X_PERCENT_DELIVERED &&
      status !== '100% delivered'
    ) {
      return <Button onClick={cancelScheduledHandler}>Cancel ongoing delivery</Button>;
    }
    return <div></div>;
  };
  const content = (
    <SpaceBetween size="l">
      <Header variant="h1" actions={getNotificationActions()}>
        {get(notificationDetailsData, 'notificationName', '')}
      </Header>
      <div>
        <NotificationsOverview details={notificationDetailsData} />
        <NotificationsDeleteModal
          onCancelHandler={onCancelDeleteEvent}
          onConfirmHandler={onConfirmDeleteEvent}
          isModalVisible={isDeleteModalVisible}
          notificationName={get(notificationDetailsData, 'notificationName', '')}
        />
      </div>
      <Box>
        <Tabs
          tabs={[
            {
              label: 'Recipients',
              id: 'first',
              content: <RecipientsTab notificationId={notificationId} />,
            },
            {
              label: 'CC',
              id: 'second',
              content: <CCTab notificationId={notificationId} />,
            },
            {
              label: 'Notification content',
              id: 'third',
              content: <NotificationContentTab templateId={notificationDetailsData.templateId} />,
            },
          ]}
        />
      </Box>
    </SpaceBetween>
  );
  return (
    <Page
      PageId={RESOURCE.PAGE_RISE_NOTIFICATIONS}
      isPageAccessControlled
      PageContent={content}
      breadCrumbItems={[
        ...BRC_ITEMS_PAGE.RISE_NOTIFICATION_DETAILS(studyPeriod, entityId, studyType),
        { text: get(notificationDetailsData, 'notificationName', '') },
      ]}
    />
  );
};

RiseNotificationsPage.propTypes = {
  history: PropTypes.object.isRequired,
  studyPeriod: PropTypes.string.isRequired,
  entityId: PropTypes.string,
  studyType: PropTypes.string.isRequired,
  deleteNotificationBegin: PropTypes.func.isRequired,
  navigateTo: PropTypes.func.isRequired,
  createNotificationScheduledBegin: PropTypes.func.isRequired,
  getNotificationDetailsBegin: PropTypes.func.isRequired,
  notificationDetailsData: PropTypes.object.isRequired,
  setNotificationStatus: PropTypes.func.isRequired,
  cancelScheduledNotification: PropTypes.func.isRequired,
  importMetadata: PropTypes.array.isRequired,
  pushNotification: PropTypes.func.isRequired,
  getImportDataBegin: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  studyPeriod: getStudyPeriod(state),
  entityId: get(state, 'entityId.entityId', undefined),
  studyType: get(state, 'studyType.studyType'),
  notificationDetailsData: get(
    state,
    'entities.notifications.getNotificationDetails.data.notification',
    {},
  ),
  importMetadata: get(state, 'entities.getAllImportMetadata.data.body.importRecords', []) || [],
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      deleteNotificationBegin: deleteNotificationAction.BEGIN,
      navigateTo: payload => push(payload),
      createNotificationScheduledBegin: createNotificationScheduleAction.BEGIN,
      getNotificationDetailsBegin: getNotificationDetailsAction.BEGIN,
      setNotificationStatus: notificationDetailActions.SET_NOTIFICATION_STATUS,
      cancelScheduledNotification: cancelScheduledNotificationAction.BEGIN,
      pushNotification: setPageNotification,
      getImportDataBegin: getAllImportMetadataAction.BEGIN,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(RiseNotificationsPage);
